'use client';

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { useVendorDiscovery } from '@/hooks/useVendorDiscovery';
import { useToast } from '@/hooks/use-toast';
import { 
  Building, 
  MapPin, 
  Star, 
  Globe,
  Users,
  Calendar,
  Clock,
  Shield,
  Award,
  Briefcase,
  Phone,
  Mail,
  ExternalLink,
  ArrowLeft
} from 'lucide-react';


export default function VendorProfilePage() {
  const params = useParams();
  const vendorId = params.vendorId as string;
  const { getVendorProfile, logProfileView } = useVendorDiscovery();
  const { toast } = useToast();
  const [vendor, setVendor] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (vendorId) {
      loadVendorProfile();
    }
  }, [vendorId]);

  const loadVendorProfile = async () => {
    try {
      setLoading(true);
      const profile = await getVendorProfile(vendorId);
      setVendor(profile);
      
      // Log profile view for analytics
      await logProfileView(vendorId, 'direct');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load vendor profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getVerificationBadge = (vendor: any) => {
    if (!vendor?.is_verified) return null;
    
    const level = vendor.verification_level;
    const colors = {
      basic: 'bg-blue-500',
      enhanced: 'bg-green-500',
      premium: 'bg-purple-500'
    };
    
    return (
      <Badge 
        className={`${colors[level as keyof typeof colors] || 'bg-gray-500'} text-white`}
      >
        <Shield className="h-3 w-3 mr-1" />
        {level?.toUpperCase()} VERIFIED
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-64 bg-muted rounded"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="h-48 bg-muted rounded"></div>
            <div className="h-48 bg-muted rounded"></div>
            <div className="h-48 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Vendor not found</h2>
            <p className="text-muted-foreground mb-4">
              The vendor profile you're looking for doesn't exist or isn't publicly available.
            </p>
            <Button asChild>
              <Link to="/vendors/search">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Search
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="space-y-6">
        {/* Back Navigation */}
        <Button variant="outline" asChild>
          <Link to="/vendors/search">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Search
          </Link>
        </Button>

        {/* Header */}
        <Card>
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row gap-6">
              <Avatar className="h-24 w-24 rounded-lg">
                <AvatarImage src={vendor.logo_url} />
                <AvatarFallback className="rounded-lg text-xl">
                  <Building className="h-12 w-12" />
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 space-y-4">
                <div>
                  <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
                    <h1 className="text-3xl font-bold">{vendor.organization_name || vendor.business_name}</h1>
                    {getVerificationBadge(vendor)}
                  </div>
                  
                  
                  {vendor.headquarters_country && (
                    <div className="flex items-center gap-2 mt-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {vendor.headquarters_state && `${vendor.headquarters_state}, `}
                        {vendor.headquarters_country}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-4">
                  {vendor.average_rating > 0 && (
                    <div className="flex items-center gap-2">
                      <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                      <span className="font-medium">{vendor.average_rating.toFixed(1)}</span>
                      <span className="text-sm text-muted-foreground">rating</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">{vendor.total_jobs_completed}</span>
                    <span className="text-sm text-muted-foreground">projects completed</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">{vendor.on_time_delivery_rate.toFixed(0)}%</span>
                    <span className="text-sm text-muted-foreground">on-time delivery</span>
                  </div>
                </div>

                {vendor.website_url && (
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <a 
                      href={vendor.website_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {vendor.website_url.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 md:items-end">
                <Button size="lg">
                  <Mail className="h-4 w-4 mr-2" />
                  Contact Vendor
                </Button>
                <Button variant="outline">
                  <Phone className="h-4 w-4 mr-2" />
                  Request Quote
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="capabilities">Capabilities</TabsTrigger>
            <TabsTrigger value="certifications">Certifications</TabsTrigger>
            <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>About</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {vendor.business_description ? (
                      <p className="whitespace-pre-wrap">{vendor.business_description}</p>
                    ) : (
                      <p className="text-muted-foreground">No description available</p>
                    )}
                  </CardContent>
                </Card>

                {vendor.value_proposition && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Value Proposition</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="whitespace-pre-wrap">{vendor.value_proposition}</p>
                    </CardContent>
                  </Card>
                )}

                {vendor.company_story && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Our Story</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="whitespace-pre-wrap">{vendor.company_story}</p>
                    </CardContent>
                  </Card>
                )}
              </div>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Company Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {vendor.founded_year && (
                      <div>
                        <Label className="text-sm font-medium">Founded</Label>
                        <p>{vendor.founded_year}</p>
                      </div>
                    )}
                    
                    {vendor.employee_count_range && (
                      <div>
                        <Label className="text-sm font-medium">Company Size</Label>
                        <p>{vendor.employee_count_range} employees</p>
                      </div>
                    )}
                    
                    {vendor.annual_revenue_range && (
                      <div>
                        <Label className="text-sm font-medium">Annual Revenue</Label>
                        <p>{vendor.annual_revenue_range}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {vendor.service_areas && Array.isArray(vendor.service_areas) && vendor.service_areas.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Service Areas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {vendor.service_areas.map((area: string, index: number) => (
                          <Badge key={index} variant="outline">
                            {area}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Capabilities */}
          <TabsContent value="capabilities">
            <Card>
              <CardHeader>
                <CardTitle>Capabilities & Specializations</CardTitle>
                <CardDescription>
                  Services and expertise offered by this vendor
                </CardDescription>
              </CardHeader>
              <CardContent>
                {vendor.vendor_capabilities && vendor.vendor_capabilities.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {vendor.vendor_capabilities.map((capability: any, index: number) => (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium">
                            {capability.capability?.name || capability.capabilities?.name || 'Unknown'}
                          </h4>
                          {capability.business_percentage > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {capability.business_percentage}%
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : vendor.service_categories && Array.isArray(vendor.service_categories) && vendor.service_categories.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {vendor.service_categories.map((category: any, index: number) => (
                      <Badge key={index} variant="secondary" className="px-3 py-2">
                        {typeof category === 'string' ? category : category.category || 'Service'}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No capabilities listed</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Certifications */}
          <TabsContent value="certifications">
            <Card>
              <CardHeader>
                <CardTitle>Certifications & Credentials</CardTitle>
                <CardDescription>
                  Professional certifications and industry credentials
                </CardDescription>
              </CardHeader>
              <CardContent>
                {vendor.vendor_certifications && vendor.vendor_certifications.length > 0 ? (
                  <div className="space-y-6">
                    {/* Group by category */}
                    {Object.entries(
                      vendor.vendor_certifications.reduce((acc: any, cert: any) => {
                        const category = cert.certification_types?.category?.name || 'Other';
                        if (!acc[category]) acc[category] = [];
                        acc[category].push(cert);
                        return acc;
                      }, {})
                    ).map(([category, certifications]: [string, any]) => (
                      <div key={category}>
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                          <Badge variant="secondary">{category}</Badge>
                        </h3>
                        <div className="space-y-3">
                          {certifications.map((cert: any, index: number) => (
                            <div key={index} className="border rounded-lg p-4">
                              <div className="flex items-start justify-between">
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold">
                                      {cert.certification_types?.name || cert.certification_name}
                                    </h4>
                                    {!cert.certification_types && (
                                      <Badge variant="outline" className="text-xs">Legacy</Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    Issued by {cert.issuing_organization}
                                  </p>
                                  {cert.issue_date && (
                                    <p className="text-xs text-muted-foreground">
                                      Issued: {new Date(cert.issue_date).toLocaleDateString()}
                                      {cert.expiry_date && ` • Expires: ${new Date(cert.expiry_date).toLocaleDateString()}`}
                                    </p>
                                  )}
                                </div>
                                <Badge 
                                  variant={cert.verification_status === 'verified' ? 'default' : 'secondary'}
                                >
                                  {cert.verification_status}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No certifications listed</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Portfolio */}
          <TabsContent value="portfolio">
            <Card>
              <CardHeader>
                <CardTitle>Portfolio & Past Work</CardTitle>
                <CardDescription>
                  Examples of previous projects and client work
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Portfolio Coming Soon</h3>
                  <p className="text-muted-foreground">
                    Portfolio showcase features will be available soon
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}